import React, { useMemo } from "react";
import { Button, Modal, Checkbox } from "antd";
import "../POS.css";

const defaultGetOptionValue = (option) =>
  option && typeof option === "object" ? option.id : option;

const defaultGetOptionLabel = (option, appConfig) => {
  if (option && typeof option === "object") {
    return appConfig?.direction === "rtl"
      ? option.name_ar || option.name || ""
      : option.name || option.name_en || "";
  }

  return String(option ?? "");
};

const normalizeSelectedValues = (values) => {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => (value && typeof value === "object" ? value.id : value))
    .filter((value) => value !== undefined && value !== null);
};

const TagOptionsModal = ({
  t,
  appConfig,
  open,
  onClose,
  product,
  selected,
  selectedValues,
  onChangeSelected,
  onConfirm,
  options,
  title,
  subtitle,
  confirmText,
  cancelText,
  width = 480,
  modalClassName = "",
  getOptionValue = defaultGetOptionValue,
  getOptionLabel,
}) => {
  const resolvedOptions = useMemo(() => {
    if (Array.isArray(options)) return options;
    if (Array.isArray(product?.tags)) return product.tags;
    return [];
  }, [options, product]);

  const resolvedSelected = useMemo(
    () => normalizeSelectedValues(selectedValues ?? selected ?? []),
    [selectedValues, selected],
  );

  const resolvedSelectedKeys = useMemo(
    () => resolvedSelected.map((value) => String(value)),
    [resolvedSelected],
  );

  const resolvedTitle = title || t("select_options");

  const resolvedSubtitle =
    subtitle ||
    (product
      ? appConfig?.direction === "rtl"
        ? product?.name || product?.name_en || ""
        : product?.name_en || product?.name || ""
      : "");

  const resolvedGetOptionLabel = (option) => {
    if (typeof getOptionLabel === "function") {
      return getOptionLabel(option);
    }
    return defaultGetOptionLabel(option, appConfig);
  };

  const toggleOption = (optionOrValue) => {
    const normalizedValue =
      optionOrValue && typeof optionOrValue === "object"
        ? getOptionValue(optionOrValue)
        : optionOrValue;

    const optionKey = String(normalizedValue);
    const nextValues = resolvedSelectedKeys.includes(optionKey)
      ? resolvedSelected.filter((item) => String(item) !== optionKey)
      : [...resolvedSelected, normalizedValue];

    onChangeSelected?.(nextValues);
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      className={`pos-option-modal ${
        appConfig?.direction === "rtl" ? "rtl" : ""
      } ${modalClassName}`.trim()}
      width={width}
      destroyOnClose={false}
    >
      <div className="option-modal-wrapper">
        <h2 className="option-modal-title">{resolvedTitle}</h2>

        {resolvedSubtitle ? (
          <h3 className="option-modal-product">{resolvedSubtitle}</h3>
        ) : null}

        <div className="option-list">
          {resolvedOptions.map((option) => {
            const optionValue = getOptionValue(option);
            const isSelected = resolvedSelectedKeys.includes(String(optionValue));

            return (
              <div
                key={String(optionValue)}
                className={`option-card ${isSelected ? "selected" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => toggleOption(optionValue)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleOption(optionValue);
                  }
                }}
              >
                <div className="option-card-clickable">
                  <Checkbox
                    checked={isSelected}
                    className="option-checkbox"
                    style={{ pointerEvents: "none" }}
                  >
                    <div className="option-label">
                      <span className="option-title">
                        {resolvedGetOptionLabel(option)}
                      </span>
                    </div>
                  </Checkbox>
                </div>
              </div>
            );
          })}
        </div>

        <div className="option-footer">
          <Button type="default" htmlType="button" className="btn-cancel" onClick={onClose}>
            {cancelText || t("cancel")}
          </Button>

          <Button type="primary" htmlType="button" className="btn-confirm" onClick={onConfirm}>
            {confirmText ||
              (resolvedSelected.length > 0
                ? t("add")
                : t("skip"))}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TagOptionsModal;
